import { cn } from "@/lib/utils";

/**
 * MUI's `LinearProgress`, ported as a shared Atlas component.
 *
 * The donor's theme carries no `MuiLinearProgress` entry
 * (`timmy/packages/react-ui/src/theme/theme.ts:297-393`), so every value here is
 * MUI's own default and that default *is* the source spec
 * (`docs/agents/migration-guidelines.md`, "Shared donor components port as
 * shared Atlas components"). Read off the installed `@mui/material@9.2.0`
 * rather than remembered:
 *
 *   - the track is 4px tall with **no radius** — `borderRadius` appears nowhere
 *     in `LinearProgress.js` — `overflow: hidden`, `position: relative`;
 *   - the track ground is `lighten(palette.primary.main, 0.62)`
 *     (`LinearProgress.js:113-118`), which over #003c77 computes to #9eb4cb;
 *   - the bar is `palette.primary.main` (`:251-257`);
 *   - **determinate moves a full-width bar by `translateX(value - 100%)`** under
 *     `transform-origin: left`, transitioned `transform .4s linear`
 *     (`TRANSITION_DURATION = 4`, `:262-268`, `:443`) — it does not animate a
 *     width, which would reflow rather than composite;
 *   - **indeterminate runs two offset bars** at `width: auto`, 2.1s each, the
 *     second delayed 1.15s (`:38-79`, `:283-296`, `:370-382`). Both live in
 *     `index.css`, as `.animate-mui-progress-indeterminate-{1,2}`, and they
 *     carry a `prefers-reduced-motion` fallback that the donor does not — MUI
 *     emits its reduced-motion rules only under a `theme.motion` key the donor
 *     never sets, so this is a named parity break, not a port.
 *
 * The two variants differ in their accessible shape as well as their paint:
 * MUI sets `aria-valuenow`/`min`/`max` only on the determinate branch
 * (`:445-447`), so an indeterminate bar announces activity without a number.
 * That is the whole reason `value` is optional here rather than defaulting to 0.
 */
export interface LinearProgressProps {
  /**
   * How far along, 0-100.
   *
   * **Omit it for the indeterminate slide.** The donor's image uploader flips
   * exactly once, at 100 (`MultipleImageUploadField.tsx:234`:
   * `variant={progress === 100 ? 'indeterminate' : 'determinate'}`), which reads
   * as the hand-off between the browser finishing its PUT and the app finishing
   * with the response.
   */
  value?: number;
  /** MUI adds no name of its own, so a caller must say what is progressing. */
  "aria-label": string;
  className?: string;
}

export function LinearProgress({ value, "aria-label": ariaLabel, className }: LinearProgressProps) {
  const indeterminate = value === undefined;

  return (
    <div
      role="progressbar"
      aria-label={ariaLabel}
      data-slot="linear-progress"
      {...(indeterminate
        ? {}
        : { "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": value })}
      className={cn("bg-lennar-progress-track relative h-1 w-full overflow-hidden", className)}
    >
      {indeterminate ? (
        <>
          <span className="bg-primary animate-mui-progress-indeterminate-1 absolute inset-y-0 w-auto" />
          <span className="bg-primary animate-mui-progress-indeterminate-2 absolute inset-y-0 w-auto" />
        </>
      ) : (
        <span
          data-slot="linear-progress-bar"
          // `transition-[transform]`, not `transition-transform`: Tailwind's
          // shorthand also transitions `translate`, `scale` and `rotate`, where
          // MUI names the one property (`getTransitionStyles(theme,
          // 'transform', …)`).
          className="bg-primary absolute inset-y-0 left-0 w-full origin-left transition-[transform] duration-[400ms] ease-linear"
          style={{ transform: `translateX(${value - 100}%)` }}
        />
      )}
    </div>
  );
}
